import { useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateEventFormData, FormErrors, TicketTier, BursaryOption } from '../../types/event/createEvent';
import { createEventService } from '../../services/event/createEventService';
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import {WaiverItem, WaiverItemType} from "@/types/event/WaiverItem";
import { usePhoneValidation } from '../common/useInputValidation';

export const useCreateEvent = () => {
    const navigate = useNavigate();
    const { getTenantPath } = useTenantNavigation();
    const { validatePhone, filterPhoneInput } = usePhoneValidation();
    
    const [formData, setFormData] = useState<CreateEventFormData>({
        title: '',
        description: '',
        image: '',
        date: '',
        location: '',
        time_start: '',
        time_end: '',
        stripe_enabled: false,
        e_transfer_enabled: false,
        contact_email: '',
        contact_number: '',
        ticket_tiers: [{ name: '', price: NaN, description: '', tickets_available: NaN }],
        bursary_enabled: false,
        bursary_type: 'custom',
        bursary_options: [{ amount: NaN, description: '' }],
        waivers_enabled: false,
        waiver_items: []
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        
        // Filter phone input to only allow valid characters
        const processedValue = name === 'contact_number' ? filterPhoneInput(value) : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : processedValue
        }));
        // Clear any existing errors for this field
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
        });
    };

    const validateForm = (): boolean => {
        let newErrors: FormErrors = {};
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (formData.description && formData.description.length > 5000) newErrors.description = "Description cannot exceed 5,000 characters";
        if (!formData.date) newErrors.date = "Date is required";
        if (!formData.location) newErrors.location = "Location is required";
        if (!formData.time_start) newErrors.time_start = "Start time is required";
        if (!formData.time_end) newErrors.time_end = "End time is required";
        if (!formData.contact_email) newErrors.contact_email = "Contact email is required";
        if (!formData.contact_number) newErrors.contact_number = "Contact number is required";

        // Validate date is in the future
        const today = new Date().toISOString().split('T')[0];
        if (formData.date < today) {
            newErrors.date = "Event date must be in the future";
        }

        // Validate end time is after start time
        if (formData.time_start && formData.time_end && formData.time_end <= formData.time_start) {
            newErrors.time_end = "End time must be after start time";
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.contact_email && !emailRegex.test(formData.contact_email)) {
            newErrors.contact_email = "Please enter a valid email address";
        }

        // Validate phone number format
        if (formData.contact_number) {
            const phoneValidationError = validatePhone(formData.contact_number);
            if (phoneValidationError) {
                newErrors.contact_number = phoneValidationError;
            }
        }

        // Validate ticket tiers
        if (!formData.ticket_tiers || formData.ticket_tiers.length === 0) {
            newErrors.ticket_tiers = "At least one ticket tier is required";
        } else {
            formData.ticket_tiers.forEach((tier, index) => {
                if (!tier.name) newErrors[`tier_${index}_name`] = 'Tier name is required';
                if (!tier.description) newErrors[`tier_${index}_description`] = 'Description is required';
                if (isNaN(tier.price)) {
                    newErrors[`tier_${index}_price`] = 'Price is required';
                } else if (tier.price < 0) {
                    newErrors[`tier_${index}_price`] = 'Price cannot be negative';
                } else if (tier.price > 0 && tier.price < 2) {
                    newErrors[`tier_${index}_price`] = 'Non-free tickets must be at least $2';
                }
                if (isNaN(tier.tickets_available)) {
                    newErrors[`tier_${index}_tickets`] = 'Tickets available is required';
                } else if (tier.tickets_available <= 0) {
                    newErrors[`tier_${index}_tickets`] = 'Tickets available must be greater than 0';
                }
            });
        }

        // Validate bursary options
        if (formData.bursary_enabled) {
            if (formData.bursary_type === 'fixed' && (!formData.bursary_options || formData.bursary_options.length === 0)) {
                newErrors.bursary_options = "At least one bursary option is required";
            } else if (formData.bursary_type === 'fixed') {
                formData.bursary_options.forEach((option, index) => {
                    if (isNaN(option.amount)) {
                        newErrors[`bursary_${index}_amount`] = 'Amount is required';
                    } else if (option.amount <= 0) {
                        newErrors[`bursary_${index}_amount`] = 'Amount must be greater than 0';
                    }
                    if (!option.description) newErrors[`bursary_${index}_description`] = 'Description is required';
                });
            }
        }

        // Validate waiver items
        if (formData.waivers_enabled && (!formData.waiver_items || formData.waiver_items.length === 0)) {
            newErrors.waiver_items = "At least one todo item is required when todo is enabled";
        } else if (formData.waivers_enabled && formData.waiver_items) {
            formData.waiver_items.forEach((item, index) => {
                if (!item.message || item.message.trim() === '') {
                    newErrors[`waiver_${index}_message`] = 'Todo message is required';
                }
                if (item.type === 'link') {
                    if (!item.link || item.link.trim() === '') {
                        newErrors[`waiver_${index}_link`] = 'Todo link is required';
                    } else {
                        // Validate URL format
                        try {
                            const url = new URL(item.link);
                            // Check if the protocol is http or https
                            if (url.protocol !== 'http:' && url.protocol !== 'https:') {
                                newErrors[`waiver_${index}_link`] = 'Link must be a valid HTTP or HTTPS URL';
                            }
                        } catch (error) {
                            newErrors[`waiver_${index}_link`] = 'Please enter a valid URL (e.g., https://example.com)';
                        }
                    }
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (): Promise<boolean> => {
        if (!validateForm()) return false;

        setLoading(true);
        try {
            await createEventService.createEvent(formData);
            setSuccessMessage('Event created successfully!');
            navigate(getTenantPath('/dashboard'));
            return true;
        } catch (error) {
            setErrors(prev => ({ 
                ...prev, 
                apiError: 'Failed to create event. Please check your information and try again.' 
            }));
            return false;
        } finally {
            setLoading(false);
        }
    };

    const addTicketTier = () => {
        setFormData(prev => ({
            ...prev,
            ticket_tiers: [...prev.ticket_tiers, { name: '', price: NaN, description: '', tickets_available: NaN }]
        }));
        // Clear all errors when adding a new tier
        setErrors({});
    };

    const removeTicketTier = (index: number) => {
        setFormData(prev => ({
            ...prev,
            ticket_tiers: prev.ticket_tiers.filter((_, i) => i !== index)
        }));
        // Clear all errors when removing a tier
        setErrors({});
    };

    const handleTicketTierChange = (index: number, field: keyof TicketTier, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            ticket_tiers: prev.ticket_tiers.map((tier, i) =>
                i === index ? { 
                    ...tier, 
                    [field]: field === 'price' || field === 'tickets_available' 
                        ? typeof value === 'string' 
                            ? value === '' 
                                ? NaN 
                                : parseFloat(value) 
                            : value 
                        : value 
                } : tier
            )
        }));
        // Clear ALL errors when changing any tier field
        setErrors({});
    };

    const addBursaryOption = () => {
        setFormData(prev => ({
            ...prev,
            bursary_options: [...prev.bursary_options, { amount: NaN, description: '' }]
        }));
        // Clear all errors when adding a new option
        setErrors({});
    };

    const removeBursaryOption = (index: number) => {
        setFormData(prev => ({
            ...prev,
            bursary_options: prev.bursary_options.filter((_, i) => i !== index)
        }));
        // Clear all errors when removing an option
        setErrors({});
    };

    const addWaiverItem = (type: WaiverItemType) => {
        setFormData(prev => ({
            ...prev,
            waiver_items: [...prev.waiver_items, { type: type, message: '', uuid: crypto.randomUUID() }]
        }));
        // Clear waiver-related errors when adding a new waiver item
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.waiver_items;
            return newErrors;
        });
    }

    const removeWaiverItem = (index: number) => {
        setFormData(prev => ({
            ...prev,
            waiver_items: prev.waiver_items.filter((_, i) => i !== index)
        }));
        // Clear waiver-related errors when removing a waiver item
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.waiver_items;
            // Also clear any specific waiver item errors
            Object.keys(newErrors).forEach(key => {
                if (key.startsWith(`waiver_${index}_`)) {
                    delete newErrors[key];
                }
            });
            return newErrors;
        });
    };

    const handleWaiverItemChange = (index: number, field: keyof WaiverItem, value: string) => {
        setFormData(prev => ({
            ...prev,
            waiver_items: prev.waiver_items.map((option, i) =>
                i === index ? {
                    ...option,
                    [field]: value
                } : option
            )
        }));
        // Clear specific field error when changing a waiver item field
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`waiver_${index}_${field}`];
            return newErrors;
        });
    }

    const handleBursaryOptionChange = (index: number, field: keyof BursaryOption, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            bursary_options: prev.bursary_options.map((option, i) =>
                i === index ? { 
                    ...option, 
                    [field]: field === 'amount' 
                        ? typeof value === 'string' 
                            ? value === '' 
                                ? NaN 
                                : parseFloat(value) 
                            : value 
                        : value 
                } : option
            )
        }));
        // Clear specific field error when changing a bursary option field
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`bursary_${index}_${field}`];
            return newErrors;
        });
    };

    return {
        formData,
        errors,
        loading,
        successMessage,
        handleChange,
        handleSubmit,
        addTicketTier,
        removeTicketTier,
        handleTicketTierChange,
        addBursaryOption,
        removeBursaryOption,
        handleBursaryOptionChange,
        addWaiverItem,
        removeWaiverItem,
        handleWaiverItemChange
    };
}; 